// Cache for filtered onboarding state results.
// Uses WeakMap keyed by data object (auto-clears when data is garbage collected),
// with nested Map keyed by keys string for O(1) lookup.
const filteredStateCache = new WeakMap<object, Map<string, unknown>>();

/**
 * Creates a cache key from the requested keys.
 */
const createCacheKey = (keys: string | string[]): string => {
	return Array.isArray(keys) ? keys.join(',') : keys;
};

/**
 * Performs the actual filtering of onboarding state data.
 */
const filterData = <
	T extends {
		onboardingState:
			| ReadonlyArray<{ key: string; value: string | null | undefined }>
			| null
			| undefined;
	},
>(
	data: T,
	requestedKeys: string | string[],
): T => {
	const keysArray = Array.isArray(requestedKeys) ? requestedKeys : [requestedKeys];
	const keySet = new Set(keysArray);

	return {
		...data,
		onboardingState: data.onboardingState?.filter(({ key }) => {
			// Check both the full key and the parsed key (without prefix)
			// Keys come back as "{prefix}:{property}" format
			const parsedKey = key.split(':')[1] || key;
			return keySet.has(key) || keySet.has(parsedKey);
		}),
	} as T;
};

/**
 * Filters the full onboarding state response to only include the requested keys.
 * This enables a centralized caching strategy where we always fetch all keys
 * but consumers only see the keys they requested.
 *
 * Results are cached per data object and keys combination to avoid redundant filtering.
 *
 * @param data - The full onboarding state response from the GraphQL query
 * @param requestedKeys - The key(s) that the consumer originally requested
 * @returns A filtered response containing only the requested keys
 */
export const filterOnboardingState = <
	T extends {
		onboardingState:
			| ReadonlyArray<{ key: string; value: string | null | undefined }>
			| null
			| undefined;
	},
>(
	data: T | null | undefined,
	requestedKeys: string | string[],
): T | undefined => {
	if (!data?.onboardingState) {
		// Convert null to undefined to maintain backward compatibility with consumers
		// that expect `T | undefined` rather than `T | null | undefined`
		return data ?? undefined;
	}

	const cacheKey = createCacheKey(requestedKeys);

	let dataCache = filteredStateCache.get(data);
	if (!dataCache) {
		dataCache = new Map<string, unknown>();
		filteredStateCache.set(data, dataCache);
	}

	if (dataCache.has(cacheKey)) {
		return dataCache.get(cacheKey) as T;
	}

	const filteredResult = filterData(data, requestedKeys);
	dataCache.set(cacheKey, filteredResult);
	return filteredResult;
};
